import type { KeyboardEvent } from 'react';

/** What a tab needs for the keyboard: its id and whether it can be chosen. */
export interface TabStop<T extends string> {
    readonly id: T;
    readonly disabled?: boolean;
}

/**
 * The one tab of a tab list that Tab reaches (roving tabindex, the ARIA tabs
 * pattern): the selected tab, or the first enabled one when none is selected.
 * Give it `tabIndex={0}` and every other tab `tabIndex={-1}`.
 */
export function resolveTabStop<T extends string>(tabs: readonly TabStop<T>[], activeTab: T | null): T | undefined {
    const active = tabs.find((tab) => tab.id === activeTab && tab.disabled !== true);
    return (active ?? tabs.find((tab) => tab.disabled !== true))?.id;
}

/**
 * The `onKeyDown` of a `role="tablist"` element: while one of its tabs has
 * focus, the arrows move focus to the previous or next enabled tab (wrapping),
 * and Home and End to the first or last. Focus only moves; Enter or Space
 * selects, as a button does. The keys it uses go no further, so page hotkeys
 * never see them; any other key, or one pressed elsewhere, passes untouched.
 */
export function handleTabListKeyDown(event: KeyboardEvent<HTMLElement>): void {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') {
        return;
    }
    const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]'))
        .filter((tab) => !(tab instanceof HTMLButtonElement && tab.disabled) && tab.getAttribute('aria-disabled') !== 'true');
    const index = tabs.indexOf(target);
    let next: HTMLElement | undefined;
    switch (event.key) {
        case 'ArrowRight':
            next = tabs[(index + 1) % tabs.length];
            break;
        case 'ArrowLeft':
            next = tabs[(index - 1 + tabs.length) % tabs.length];
            break;
        case 'Home':
            next = tabs[0];
            break;
        case 'End':
            next = tabs[tabs.length - 1];
            break;
        default:
            return;
    }
    event.preventDefault();
    event.stopPropagation();
    next?.focus();
}
